import { useFeaturePageCatalog } from "@/features/feature-page/feature-page-catalog-context";
import { FeaturePageCitations } from "@/features/feature-page/feature-page-citations";
import { FeaturePageSection } from "@/features/feature-page/feature-page-section";
import { FeaturePageVisual } from "@/features/feature-page/feature-page-visual";
import type { ResolvedFeaturePageBlock } from "@garimpa/api/featurePages/types";

export function TimelineBlock({
  block,
}: {
  block: Extract<ResolvedFeaturePageBlock, { type: "timeline" }>;
}) {
  const catalog = useFeaturePageCatalog();
  const fallbackHeadingId = block.heading ? undefined : `${block.id}-heading`;
  return (
    <FeaturePageSection
      block={block}
      resolvedBackground={block.resolvedBackground}
      labelledById={fallbackHeadingId}
    >
      {block.heading ? (
        <h2
          id={`${block.id}-heading`}
          className="mb-12 text-center text-3xl font-bold tracking-tight sm:text-5xl"
        >
          {block.heading}
        </h2>
      ) : null}
      <ol className="relative border-l border-border pl-7 md:pl-10">
        {block.items.map((item, index) => {
          const visual = block.resolvedItemVisuals?.[index];
          const entity =
            item.visual?.kind === "catalogEntity"
              ? catalog.getEntityByRef(item.visual.catalogRefId)
              : undefined;
          const ItemHeading = block.heading ? "h3" : "h2";
          return (
            <li key={`${item.label}-${item.title}`} className="relative pb-12 last:pb-0">
              <span className="absolute -left-[2.05rem] top-2 size-3 rounded-full bg-primary ring-4 ring-background md:-left-[2.8rem]" />
              <p className="text-sm font-bold uppercase tracking-wider text-primary">
                {item.label}
              </p>
              <div className="mt-2 grid gap-6 md:grid-cols-[1fr_auto]">
                <div>
                  <ItemHeading
                    id={index === 0 ? fallbackHeadingId : undefined}
                    className="text-2xl font-bold"
                  >
                    {item.title}
                  </ItemHeading>
                  <p className="mt-3 max-w-3xl leading-7 text-muted-foreground">
                    {item.body}
                    <FeaturePageCitations sourceIds={item.sourceIds} />
                  </p>
                </div>
                {visual && entity ? (
                  <a
                    href={entity.href}
                    className="group block w-40 overflow-hidden rounded-xl border border-border bg-secondary shadow-sm transition hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-52 md:w-44"
                  >
                    <FeaturePageVisual
                      visual={visual}
                      className="aspect-square w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                    />
                  </a>
                ) : visual ? (
                  <FeaturePageVisual
                    visual={visual}
                    className="aspect-square w-40 rounded-xl object-cover shadow-sm sm:w-52 md:w-44"
                  />
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </FeaturePageSection>
  );
}
